import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Timeline } from 'primereact/timeline';
import { Toast } from 'primereact/toast';
import { InputTextarea } from 'primereact/inputtextarea';
import { AutoComplete } from 'primereact/autocomplete';
import { 
  getTicket, 
  addNote, 
  updateTicketStatus, 
  updateTicket, 
  getPurchaseOrders,
  uploadTicketAttachment,
  removeTicketAttachment,
  downloadTicketAttachment
} from '../api';
import { Ticket, TicketStatus, TicketPriority, TicketActivityAction } from '../types/ticket';

const TicketDetails: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = React.useRef<Toast>(null);
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  const [filteredPOs, setFilteredPOs] = useState<any[]>([]);
  const [selectedPO, setSelectedPO] = useState<any>(null);
  const [searching, setSearching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadTicket();
  }, [id]);

  const loadTicket = async () => {
    try {
      if (!id) return;
      const data = await getTicket(Number(id));
      setTicket(data);
    } catch (error) {
      console.error('Error loading ticket:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load ticket',
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !id) return;

    setSubmittingNote(true);
    try {
      await addNote(Number(id), { content: newNote });
      await loadTicket(); // Reload ticket to get updated notes
      setNewNote('');
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Note added successfully',
        life: 3000
      });
    } catch (error) {
      console.error('Error adding note:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to add note',
        life: 3000
      });
    } finally {
      setSubmittingNote(false);
    }
  };

  const getPriorityTag = (priority: TicketPriority) => {
    const severityMap: Record<TicketPriority, 'success' | 'warning' | 'danger'> = {
      [TicketPriority.LOW]: 'success',
      [TicketPriority.MEDIUM]: 'warning',
      [TicketPriority.HIGH]: 'danger'
    };

    return (
      <Tag 
        value={priority} 
        severity={severityMap[priority]}
      />
    );
  };

  const getStatusTag = (status: TicketStatus) => {
    const severityMap: Record<TicketStatus, 'info' | 'warning' | 'success'> = {
      [TicketStatus.OPEN]: 'info',
      [TicketStatus.IN_PROGRESS]: 'warning',
      [TicketStatus.CLOSED]: 'success'
    };

    return (
      <Tag 
        value={status} 
        severity={severityMap[status]}
      />
    );
  };

  const getTimelineEvents = (ticket: Ticket) => {
    const events = ticket.activityLogs.map(log => {
      const iconMap: Record<TicketActivityAction, string> = {
        [TicketActivityAction.CREATED]: 'pi pi-plus',
        [TicketActivityAction.EDITED]: 'pi pi-pencil',
        [TicketActivityAction.STATUS_CHANGE]: 'pi pi-sync',
        [TicketActivityAction.PO_LINK]: 'pi pi-link',
        [TicketActivityAction.PO_UNLINK]: 'pi pi-link',
        [TicketActivityAction.NOTE_ADDED]: 'pi pi-comment',
        [TicketActivityAction.ATTACHMENT_ADDED]: 'pi pi-paperclip',
        [TicketActivityAction.ATTACHMENT_REMOVED]: 'pi pi-times',
      };

      const colorMap: Record<TicketActivityAction, string> = {
        [TicketActivityAction.CREATED]: '#4CAF50',
        [TicketActivityAction.EDITED]: '#FF9800',
        [TicketActivityAction.STATUS_CHANGE]: '#9C27B0',
        [TicketActivityAction.PO_LINK]: '#2196F3',
        [TicketActivityAction.PO_UNLINK]: '#F44336',
        [TicketActivityAction.NOTE_ADDED]: '#00BCD4',
        [TicketActivityAction.ATTACHMENT_ADDED]: '#607D8B',
        [TicketActivityAction.ATTACHMENT_REMOVED]: '#795548',
      };

      return {
        status: `${log.action} by ${log.user.username}`,
        date: new Date(log.timestamp).toLocaleString(),
        icon: iconMap[log.action] || 'pi pi-info-circle',
        color: colorMap[log.action] || '#757575',
        content: log.details
      };
    });

    return events;
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!ticket) {
    return <div>Ticket not found</div>;
  }

  const searchPurchaseOrders = async (event: { query: string }) => {
    try {
      setSearching(true);
      const [pos] = await getPurchaseOrders({
        page: 1,
        limit: 10,
        search: event.query,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });
      setFilteredPOs(pos.map(po => ({
        ...po,
        label: `#${po.poNumber}`,
        value: po.id
      })));
    } catch (error) {
      console.error('Error searching purchase orders:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to search purchase orders',
        life: 3000
      });
    } finally {
      setSearching(false);
    }
  };

  const handlePurchaseOrderSelect = async () => {
    if (!ticket || !selectedPO) return;

    try {
      await updateTicket(ticket.id, {
        purchaseOrderId: selectedPO.id
      });
      await loadTicket();
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Purchase order linked successfully',
        life: 3000
      });
    } catch (error) {
      console.error('Error linking purchase order:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to link purchase order',
        life: 3000
      });
    }
  };

  const handlePurchaseOrderUnlink = async () => {
    if (!ticket) return;

    try {
      await updateTicket(ticket.id, {
        purchaseOrderId: undefined
      });
      await loadTicket();
      setSelectedPO(null);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Purchase order unlinked successfully',
        life: 3000
      });
    } catch (error) {
      console.error('Error unlinking purchase order:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to unlink purchase order',
        life: 3000
      });
    }
  };

  return (
    <div className="card">
      <Toast ref={toast} />
      
      <div className="flex justify-content-between mb-3">
        <h1>Ticket Details</h1>
        <div className="flex gap-2">
          {ticket.status !== TicketStatus.CLOSED ? (
            <Button
              label="Close Ticket"
              icon="pi pi-lock"
              className="p-button-warning"
              onClick={async () => {
                try {
                  await updateTicketStatus(ticket.id, TicketStatus.CLOSED);
                  await loadTicket();
                  toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Ticket closed successfully',
                    life: 3000
                  });
                } catch (error) {
                  console.error('Error closing ticket:', error);
                  toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to close ticket',
                    life: 3000
                  });
                }
              }}
            />
          ) : (
            <Button
              label="Reopen Ticket"
              icon="pi pi-lock-open"
              className="p-button-info"
              onClick={async () => {
                try {
                  await updateTicketStatus(ticket.id, TicketStatus.OPEN);
                  await loadTicket();
                  toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Ticket reopened successfully',
                    life: 3000
                  });
                } catch (error) {
                  console.error('Error reopening ticket:', error);
                  toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to reopen ticket',
                    life: 3000
                  });
                }
              }}
            />
          )}
          <Button
            label="Edit"
            icon="pi pi-pencil"
            onClick={() => navigate(`/tickets/edit/${ticket.id}`)}
          />
          <Button
            label="Back"
            icon="pi pi-arrow-left"
            className="p-button-secondary"
            onClick={() => navigate('/tickets')}
          />
        </div>
      </div>

      <div className="grid">
        <div className="col-12 md:col-8">
          <Card>
            <div className="flex flex-column gap-3">
              <div className="flex justify-content-between align-items-center">
                <h2 className="m-0">{ticket.title}</h2>
                <div className="flex gap-2 align-items-center">
                  {getPriorityTag(ticket.priority)}
                  {getStatusTag(ticket.status)}
                  {ticket.purchaseOrder && (
                    <Tag 
                      severity="info"
                      value={`PO: #${ticket.purchaseOrder.poNumber}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/purchase-orders/${ticket.purchaseOrder?.id}`)}
                    />
                  )}
                </div>
              </div>

              <div className="text-500">
                Created by {ticket.createdBy.username} on {new Date(ticket.createdAt).toLocaleDateString()}
              </div>

              {ticket.assignedTo && (
                <div className="text-500">
                  Assigned to {ticket.assignedTo.username}
                </div>
              )}

              <div className="surface-100 border-round p-3">
                <h3>Description</h3>
                <p className="white-space-pre-line">{ticket.description}</p>
              </div>

              <div className="surface-100 border-round p-3">
                <h3>Purchase Order</h3>
                {ticket.purchaseOrder ? (
                      <div className="flex align-items-center gap-2">
                        <span>Linked to PO <Link to={`/purchase-orders/${ticket.purchaseOrder.id}`} className="text-primary">#{ticket.purchaseOrder.poNumber}</Link></span>
                    <Button
                      icon="pi pi-link-alt"
                      rounded
                      text
                      severity="danger"
                      onClick={handlePurchaseOrderUnlink}
                      tooltip="Unlink Purchase Order"
                    />
                  </div>
                ) : (
                  <div className="flex align-items-center gap-2">
                    <AutoComplete
                      value={selectedPO}
                      suggestions={filteredPOs}
                      completeMethod={searchPurchaseOrders}
                      field="label"
                      placeholder="Search Purchase Orders"
                      className="w-full md:w-20rem"
                      onChange={(e) => setSelectedPO(e.value)}
                      disabled={searching}
                    />
                    <Button
                      icon="pi pi-link"
                      rounded
                      severity="info"
                      disabled={!selectedPO}
                      onClick={handlePurchaseOrderSelect}
                      tooltip="Link Purchase Order"
                    />
                  </div>
                )}
              </div>

              <div className="flex flex-column gap-2">
                <h3>Add Note</h3>
                <InputTextarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={3}
                  autoResize
                  className="w-full"
                />
                <Button
                  label="Add Note"
                  icon="pi pi-plus"
                  onClick={handleAddNote}
                  loading={submittingNote}
                  disabled={!newNote.trim()}
                />
              </div>

              <div>
                <h3>Activity Timeline</h3>
                <Timeline
                  value={getTimelineEvents(ticket)}
                  content={(item: any) => (
                    <div className="flex flex-column gap-1">
                      <span className="font-bold">{item.status}</span>
                      <span className="text-500">{item.date}</span>
                      {item.content && (
                        <div className="surface-100 border-round p-2 mt-2">
                          {item.content}
                        </div>
                      )}
                    </div>
                  )}
                />
              </div>
            </div>
              <div className="surface-100 border-round p-3">
                <div className="flex justify-content-between align-items-center mb-3">
                  <h3 className="m-0">Attachments</h3>
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file || !ticket) return;

                        setUploading(true);
                        try {
                          await uploadTicketAttachment(ticket.id, file);
                          await loadTicket();
                          toast.current?.show({
                            severity: 'success',
                            summary: 'Success',
                            detail: 'File uploaded successfully',
                            life: 3000
                          });
                        } catch (error) {
                          console.error('Error uploading file:', error);
                          toast.current?.show({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to upload file',
                            life: 3000
                          });
                        } finally {
                          setUploading(false);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                        }
                      }}
                    />
                    <Button
                      label="Upload"
                      icon="pi pi-upload"
                      loading={uploading}
                      onClick={() => fileInputRef.current?.click()}
                    />
                  </div>
                </div>
                
                {ticket.attachments && ticket.attachments.length > 0 ? (
                  <div className="flex flex-column gap-2">
                    {ticket.attachments.map((filename) => (
                      <div key={filename} className="flex align-items-center justify-content-between surface-50 p-3 border-round">
                        <span className="flex align-items-center gap-2">
                          <i className="pi pi-file" />
                          {filename}
                        </span>
                        <div className="flex gap-2">
                          <Button
                            icon="pi pi-download"
                            rounded
                            text
                            onClick={() => downloadTicketAttachment(ticket.id, filename)}
                            tooltip="Download"
                          />
                          <Button
                            icon="pi pi-times"
                            rounded
                            text
                            severity="danger"
                            onClick={async () => {
                              try {
                                await removeTicketAttachment(ticket.id, filename);
                                await loadTicket();
                                toast.current?.show({
                                  severity: 'success',
                                  summary: 'Success',
                                  detail: 'File removed successfully',
                                  life: 3000
                                });
                              } catch (error) {
                                console.error('Error removing file:', error);
                                toast.current?.show({
                                  severity: 'error',
                                  summary: 'Error',
                                  detail: 'Failed to remove file',
                                  life: 3000
                                });
                              }
                            }}
                            tooltip="Remove"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-500 m-0">No attachments</p>
                )}
              </div>

          </Card>
        </div>

        <div className="col-12 md:col-4">
          <Card title="Activity">
            <div className="flex flex-column gap-2">
              <div className="flex justify-content-between">
                <span>Created</span>
                <span>{new Date(ticket.createdAt).toLocaleString()}</span>
              </div>
              <div className="flex justify-content-between">
                <span>Last Updated</span>
                <span>{new Date(ticket.updatedAt).toLocaleString()}</span>
              </div>
              <div className="flex justify-content-between">
                <span>Status</span>
                {getStatusTag(ticket.status)}
              </div>
              <div className="flex justify-content-between">
                <span>Priority</span>
                {getPriorityTag(ticket.priority)}
              </div>
              <div className="flex justify-content-between">
                <span>Notes</span>
                <span>{ticket.notes.length}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TicketDetails;
